import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Divider,
  Button,
  TextField,
  InputAdornment,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ACCOUNT_DETAIL_FONT_SX } from '../../../components/AccountDetailPolicy/accountDetailTypography';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useDispatch, useSelector } from 'react-redux';
import { editComplianceWorkload, clearAllComplianceMessages } from '../../../store/compliance/complianceSlice';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { getComplianceTags as getComplianceTagsApi } from '../../../helpers/auth_aws_helper';

const EditWorkloadModal = ({ open, onClose, workload, onSuccess, accountNumber: accountNumberProp }) => {
  const dispatch = useDispatch();
  const [workloadName, setWorkloadName] = useState('');
  const [tags, setTags] = useState([{ key: '', value: '' }]);
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Tag API local cache and loading/error state
  const [tagKeysByAccount, setTagKeysByAccount] = useState({}); // { accountNumber: [keys] }
  const [tagValuesByAccountAndKey, setTagValuesByAccountAndKey] = useState({}); // { 'account:key': [values] }
  const [loadingTagKeys, setLoadingTagKeys] = useState(false);
  const [loadingTagValues, setLoadingTagValues] = useState({}); // { 'account:key': bool }
  const [tagApiError, setTagApiError] = useState('');

  const { editComplianceWorkloadMessage, editComplianceWorkloadError } = useSelector(state => state.compliance);

  // Validation
  const errors = {
    workloadName: !workloadName.trim(),
    tagKey1: !tags[0].key.trim(),
    tagValue1: !tags[0].value.trim(),
    tagKey2: tags[1] ? !tags[1].key.trim() && !!tags[1].value.trim() : false,
    tagValue2: tags[1] ? !tags[1].value.trim() && !!tags[1].key.trim() : false,
  };
  const hasError = Object.values(errors).some(Boolean);

  const handleBlur = (field) => setTouched(t => ({ ...t, [field]: true }));

  // Reset value if key changes
  const handleTagChange = (idx, field, value) => {
    setTags(prev => prev.map((tag, i) => {
      if (i !== idx) return tag;
      if (field === 'key') {
        return { ...tag, key: value, value: '' };
      }
      return { ...tag, [field]: value };
    }));
  };

  const handleAddTag = () => {
    if (tags.length < 2) setTags([...tags, { key: '', value: '' }]);
  };

  const handleRemoveTag = () => {
    if (tags.length > 1) setTags(tags.slice(0, 1));
  };

  const handleUpdate = () => {
    if (hasError) return;
    const payload = {
      scheduleId: workload.scheduleId,
      scheduleName: workloadName,
      scheduleType: workload.scheduleType,
      frequency: workload.frequency,
      resourceScopeType: 'tag_based',
      tag: tags
        .filter(tag => tag.key.trim() && tag.value.trim())
        .map(tag => ({ key: tag.key.trim(), value: tag.value.trim() })),
    };
    dispatch(editComplianceWorkload(payload));
  };

  // Use accountNumber prop if provided, otherwise fallback to workload.accountNumber
  const accountNumber = typeof accountNumberProp !== 'undefined' ? accountNumberProp : workload?.accountNumber;

  // Fetch tag keys for account
  const fetchTagKeys = async (accountNumber) => {
    if (!accountNumber || tagKeysByAccount[accountNumber]) return;
    setLoadingTagKeys(true);
    setTagApiError('');
    try {
      const res = await getComplianceTagsApi({ account_number: accountNumber });
      const keys = Array.isArray(res.keys) ? res.keys : [];
      setTagKeysByAccount(prev => ({ ...prev, [accountNumber]: keys }));
    } catch (err) {
      setTagApiError(err?.message || 'Failed to fetch tag keys');
    } finally {
      setLoadingTagKeys(false);
    }
  };

  // Fetch tag values for (account, key)
  const fetchTagValues = async (accountNumber, key) => {
    const cacheKey = `${accountNumber}:${key}`;
    if (!accountNumber || !key || tagValuesByAccountAndKey[cacheKey]) return;
    setLoadingTagValues(prev => ({ ...prev, [cacheKey]: true }));
    setTagApiError('');
    try {
      const res = await getComplianceTagsApi({ account_number: accountNumber, tag_key: key });
      const values = Array.isArray(res[key]) ? res[key] : [];
      setTagValuesByAccountAndKey(prev => ({ ...prev, [cacheKey]: values }));
    } catch (err) {
      setTagApiError(err?.message || 'Failed to fetch tag values');
    } finally {
      setLoadingTagValues(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  // On modal open, fetch tag keys for this account
  useEffect(() => {
    if (open && accountNumber) {
      fetchTagKeys(accountNumber);
    }
    // eslint-disable-next-line
  }, [open, accountNumber]);

  // On tag key change, fetch tag values for that key
  useEffect(() => {
    tags.forEach((tag, idx) => {
      if (tag.key && accountNumber) {
        fetchTagValues(accountNumber, tag.key);
      }
    });
    // eslint-disable-next-line
  }, [tags.map(t => t.key).join(','), accountNumber]);

  // Reset tag cache and errors on modal close
  useEffect(() => {
    if (!open) {
      setTagKeysByAccount({});
      setTagValuesByAccountAndKey({});
      setLoadingTagKeys(false);
      setLoadingTagValues({});
      setTagApiError('');
    }
  }, [open]);

  useEffect(() => {
    if (open && workload) {
      // Pre-fill form fields when modal opens
      setWorkloadName(workload.scheduleName || '');
      setTags(workload.tag?.length ? workload.tag : [{ key: '', value: '' }]);
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      dispatch(clearAllComplianceMessages());
    }
    if (!open) {
      setSnackbar({ open: false, message: '', severity: 'error' });
      dispatch(clearAllComplianceMessages());
    }
  }, [open, workload, dispatch]);

  useEffect(() => {
    if (editComplianceWorkloadMessage) {
      if (onSuccess) onSuccess(editComplianceWorkloadMessage);
      dispatch(clearAllComplianceMessages());
      onClose();
    } else if (editComplianceWorkloadError) {
      setSnackbar({ open: true, message: editComplianceWorkloadError, severity: 'error' });
      dispatch(clearAllComplianceMessages());
    }
    // eslint-disable-next-line
  }, [editComplianceWorkloadMessage, editComplianceWorkloadError, dispatch, onSuccess, onClose]);

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="edit-workload-modal"
      aria-describedby="modal-for-editing-workload"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 520,
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: 24,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          ...ACCOUNT_DETAIL_FONT_SX,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#F5F5F5',
            padding: '16px 24px',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
            borderBottom: '1px solid #E0E0E0',
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Edit Workload
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        {/* Content */}
        <Box sx={{ p: 3 }}>
          <TextField
            label="Account Number"
            value={accountNumber || ''}
            disabled
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Workload Name *"
            value={workloadName}
            disabled
            fullWidth
            sx={{ mb: 0.5 }}
            error={touched.workloadName && errors.workloadName}
            helperText={touched.workloadName && errors.workloadName ? 'Workload Name is required' : ''}
            inputProps={{ maxLength: 128 }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
            A workload name is unique per account.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {tags.map((tag, idx) => {
              const tagKeys = tagKeysByAccount[accountNumber] || [];
              const cacheKey = `${accountNumber}:${tag.key}`;
              const tagValues = tagValuesByAccountAndKey[cacheKey] || [];
              return (
                <Box key={idx} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Autocomplete
                    freeSolo
                    options={tagKeys}
                    loading={loadingTagKeys}
                    value={tag.key}
                    onInputChange={(_, newValue) => handleTagChange(idx, 'key', newValue)}
                    onBlur={() => handleBlur(`tagKey${idx + 1}`)}
                    fullWidth
                    ListboxProps={{
                      style: {
                        width: 400,
                        maxHeight: 200,
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <span style={{ display: 'inline-block', minWidth: 600, whiteSpace: 'nowrap' }}>{option}</span>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Tag Key${idx === 0 ? ' *' : ''}`}
                        sx={{ flex: 1, minWidth: 0, maxWidth: 240 }}
                        error={touched[`tagKey${idx + 1}`] && (idx === 0 ? errors.tagKey1 : errors.tagKey2)}
                        helperText={touched[`tagKey${idx + 1}`] && (idx === 0 ? errors.tagKey1 ? 'Tag Name is required' : '' : errors.tagKey2 ? 'Tag Name is required if Tag Value is filled' : '')}
                        inputProps={{ ...params.inputProps, maxLength: 64 }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingTagKeys ? (
                                <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Autocomplete
                    freeSolo
                    options={tagValues}
                    loading={!!loadingTagValues[cacheKey]}
                    value={tag.value}
                    onInputChange={(_, newValue) => handleTagChange(idx, 'value', newValue)}
                    onBlur={() => handleBlur(`tagValue${idx + 1}`)}
                    fullWidth
                    ListboxProps={{
                      style: {
                        width: 400,
                        maxHeight: 200,
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <span style={{ display: 'inline-block', minWidth: 600, whiteSpace: 'nowrap' }}>{option}</span>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Tag Value${idx === 0 ? ' *' : ''}`}
                        sx={{ flex: 1, minWidth: 0, maxWidth: 240 }}
                        error={touched[`tagValue${idx + 1}`] && (idx === 0 ? errors.tagValue1 : errors.tagValue2)}
                        helperText={touched[`tagValue${idx + 1}`] && (idx === 0 ? errors.tagValue1 ? 'Tag Value is required' : '' : errors.tagValue2 ? 'Tag Value is required if Tag Name is filled' : '')}
                        inputProps={{ ...params.inputProps, maxLength: 128 }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {!!loadingTagValues[cacheKey] ? (
                                <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {idx === 0 && tags.length < 2 ? (
                      <IconButton onClick={handleAddTag} sx={{ color: '#1976d2' }} aria-label="Add tag">
                        <AddCircleOutlineIcon />
                      </IconButton>
                    ) : idx === 1 ? (
                      <IconButton onClick={handleRemoveTag} sx={{ color: '#d32f2f' }} aria-label="Remove tag">
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    ) : (
                      <IconButton sx={{ opacity: 0, pointerEvents: 'none' }}>
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {/* Tag API error snackbar inside modal, below tags */}
          {tagApiError && (
            <Snackbar
              open={!!tagApiError}
              autoHideDuration={6000}
              onClose={() => setTagApiError('')}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              sx={{ position: 'relative', mt: 2 }}
            >
              <Alert onClose={() => setTagApiError('')} severity="error" sx={{ width: '100%' }}>
                {tagApiError}
              </Alert>
            </Snackbar>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={hasError}
              onClick={handleUpdate}
            >
              Update
            </Button>
          </Box>
        </Box>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  );
};

export default EditWorkloadModal; 